import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { PowerQuizButton } from "@/components/grow/PowerQuizButton";
import { usePowerQuizStatus } from "@/components/grow/use-power-quiz-status";
import { PowerQuizStatus } from "@/components/grow/PowerQuizStatus";
import { SmartAnalysisButton } from "@/components/grow/SmartAnalysisButton";
import { AnalysedDocumentCount } from "@/components/grow/AnalysedDocumentCount";
import { Tooltip } from "@/components/common/Tooltip";
import { useAnalysedDocumentCount } from "@/components/grow/use-analysed-document-count";
import { BackButton } from "@/components/common/BackButton";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Disclosure } from "@/components/design/Disclosure";
import { buttonClasses } from "@/components/design/button-classes";
import { PowersDataDocument } from "@/graphql/generated/operations";
import { ELEMENT_CODES, elementImageSrc } from "@/lib/elements";
import { elementDescription } from "@/lib/element-descriptions";
import { elementEvaluation } from "@/lib/element-evaluations";
import { POWER_BY_ABBR, TAG_LABEL_IDS } from "@/lib/power-library";
import { combineLevels } from "@/lib/score-source";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { useOpenDocument } from "@/lib/use-open-document";

const ELEMENT_BY_CODE = new Map(
  Object.entries(ELEMENT_CODES).map(([element, code]) => [code, element]),
);

// `rationale` is server data, not UI copy: genesis passes through whatever document-extraction
// wrote. When a trait cannot be scored, document-extraction stamps one of three fixed ENGLISH
// sentences into that field (TraitScoringStage.kt:87, :152/:175, :200) instead of a model-written
// rationale — which is how "No evidence found for this trait." came to sit under the German
// heading "Was Smart Analyse gefunden hat". Translating the heading cannot fix that; the sentence
// has to be recognised and replaced. Anything not in this table is a real rationale and is shown
// verbatim, in whatever language the model wrote it.
const UNSCORED_RATIONALES: Record<
  string,
  { id: string; defaultMessage: string }
> = {
  "No evidence found for this trait.": {
    id: "grow.powerDetail.rationaleNoEvidence",
    defaultMessage: "No evidence for this Power was found in your documents.",
  },
  "The evidence did not support a reliable rating for this trait.": {
    id: "grow.powerDetail.rationaleNoConfidence",
    defaultMessage:
      "The evidence found was not enough to rate this Power reliably.",
  },
  "Scoring could not be completed.": {
    id: "grow.powerDetail.rationaleScoringFailed",
    defaultMessage: "This Power could not be analysed. Run the analysis again.",
  },
};

export function PowerDetailPage() {
  const { abbr = "" } = useParams();
  const intl = useIntl();
  const canUpload = useHasFeature(FEATURE_CATALOG.saveDocuments.key);
  const documentCount = useAnalysedDocumentCount(canUpload);
  const { data, loading, error } = useQuery(PowersDataDocument, {
    fetchPolicy: "cache-and-network",
  });
  const [openError, setOpenError] = useState<string | null>(null);
  const openDocument = useOpenDocument(setOpenError);
  const element = ELEMENT_BY_CODE.get(abbr);
  const quizStatus = usePowerQuizStatus(element);
  const power = POWER_BY_ABBR[abbr];
  const name = power ? intl.formatMessage({ id: power.nameId }) : abbr;
  const self = data?.selfAssessmentResults.find(
    (r) => String(r.element) === element,
  );
  const smart = data?.smartAnalysisResults.find(
    (r) => String(r.element) === element,
  );
  const selfScore = typeof self?.result === "number" ? self.result : null;
  const smartScore = typeof smart?.score === "number" ? smart.score : null;
  const serverRationale = smart?.rationale?.trim() ?? "";
  const unscoredRationale = UNSCORED_RATIONALES[serverRationale];
  const rationale = unscoredRationale
    ? intl.formatMessage(unscoredRationale)
    : serverRationale || null;
  const score = element
    ? (combineLevels(
        new Map([[element, selfScore]]),
        new Map([[element, smartScore]]),
      ).get(element) ?? null)
    : null;
  const evaluation = element
    ? elementEvaluation(element, score, intl.locale)
    : null;
  const description = element ? elementDescription(element, intl.locale) : null;
  const imageSrc = element ? elementImageSrc(element) : null;
  const progress =
    self && self.questionCount > 0
      ? self.answeredQuestionCount / self.questionCount
      : 0;
  const evidences = smart?.evidences ?? [];

  // Agent context intentionally excludes document-derived findings.
  useAssistantContext(
    element
      ? [
          {
            description: "power detail page the user is viewing",
            value:
              `power "${name}" (${abbr}). ` +
              (self
                ? `self-assessment score ${self.result ?? "unrated"}, ${self.answeredQuestionCount}/${self.questionCount} statements answered`
                : "not self-assessed yet") +
              " (self-assessment is the only power score available to you).",
          },
        ]
      : null,
  );
  useAssistantTour(
    element
      ? {
          label: intl.formatMessage({
            id: "grow.powerDetail.tour.label",
            defaultMessage: "Grow Power",
          }),
          summary: intl.formatMessage(
            {
              id: "grow.powerDetail.tour.summaryStatus",
              defaultMessage:
                "You have rated {answered} of {total} in-depth statements.",
            },
            {
              answered: self?.answeredQuestionCount ?? 0,
              total: self?.questionCount ?? 0,
            },
          ),
          stops: [
            {
              anchor: "grow.power.description",
              title: name,
              description: description ?? name,
            },
            {
              anchor: "grow.power.selfEvaluation",
              title: intl.formatMessage({
                id: "grow.powerDetail.selfAssessment",
                defaultMessage: "Self-assessment",
              }),
              description: intl.formatMessage(
                {
                  id: "grow.powerDetail.tour.summaryStatus",
                  defaultMessage:
                    "You have rated {answered} of {total} in-depth statements.",
                },
                {
                  answered: self?.answeredQuestionCount ?? 0,
                  total: self?.questionCount ?? 0,
                },
              ),
            },
          ],
        }
      : null,
  );

  if (!element || !power)
    return (
      <main className="max-w-[1600px] px-4 py-6 @sm:px-6 @lg:px-10">
        <BackButton />
      </main>
    );
  const selfAction =
    self &&
    self.questionCount > 0 &&
    self.answeredQuestionCount === self.questionCount ? (
      <FormattedMessage
        id="grow.powerDetail.repeatSelfAssessment"
        defaultMessage="Review in-depth assessment"
      />
    ) : self && self.answeredQuestionCount > 0 ? (
      <FormattedMessage
        id="grow.powerDetail.continueSelfAssessment"
        defaultMessage="Continue in-depth assessment"
      />
    ) : (
      <FormattedMessage
        id="grow.powerDetail.startSelfAssessment"
        defaultMessage="Start in-depth assessment"
      />
    );

  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="PowersData, PowerAnalysisDocuments, PublishedQuestionnaireGroupParts, QuestionnaireGroupEvaluation"
    >
      <div className="mb-6 flex items-center gap-3">
        <BackButton />
        <span
          id="grow-power-eyebrow"
          className="text-smycles-micro tracking-[0.25em] text-ink-muted"
        >
          <FormattedMessage
            id="grow.powerDetail.eyebrow"
            defaultMessage="Power · {abbr}"
            values={{ abbr }}
          />
        </span>
      </div>
      <div className="grid grid-cols-1 items-start gap-8 @lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] @2xl:grid-cols-[minmax(280px,440px)_minmax(0,1fr)]">
        <aside data-assistant-anchor="grow.power.description">
          {imageSrc ? (
            <div
              className="box relative aspect-[4/3] overflow-hidden"
              data-assistant-anchor="grow.power.artwork"
            >
              <img
                src={imageSrc}
                alt=""
                className="absolute inset-0 h-full w-full scale-110 object-cover transition-all duration-700"
                style={{ filter: `blur(${20 * (1 - progress)}px)` }}
              />
            </div>
          ) : null}
          <h1 className="smycles-page-title mt-4">{name}</h1>
          <p className="smycles-field-label mt-1 text-ink">
            {intl.formatMessage({ id: TAG_LABEL_IDS[power.tag] })}
          </p>
          {description ? (
            <p className="mt-3 max-w-[65ch] text-smycles-body leading-relaxed text-ink">
              {description}
            </p>
          ) : null}
          <div className="mt-4 flex flex-col items-start gap-2">
            <AskAssistantButton
              variant="secondary"
              prompt={() =>
                intl.formatMessage(
                  {
                    id: `grow.powerDetail.askIntro.${Math.floor(Math.random() * 10)}`,
                  },
                  { name },
                )
              }
              label={
                <FormattedMessage
                  id="grow.powerDetail.askButton"
                  defaultMessage="Understand {name}"
                  values={{ name }}
                />
              }
            />
            <AskAssistantButton
              variant="secondary"
              prompt={() =>
                intl.formatMessage(
                  {
                    id: `grow.powerDetail.askImprove.${Math.floor(Math.random() * 10)}`,
                  },
                  { name },
                )
              }
              label={
                <FormattedMessage
                  id="grow.powerDetail.askImproveButton"
                  defaultMessage="How do I improve {name}?"
                  values={{ name }}
                />
              }
            />
          </div>
        </aside>
        <section className="box overflow-hidden bg-surface">
          <div className="p-6">
            {loading && !data ? (
              <p className="text-smycles-body text-ink-muted">
                <FormattedMessage
                  id="common.loading"
                  defaultMessage="Loading…"
                />
              </p>
            ) : error && !data ? (
              <p className="text-smycles-body text-ink-muted">
                <FormattedMessage
                  id="grow.powerDetail.loadError"
                  defaultMessage="Your Power result could not be loaded."
                />
              </p>
            ) : score === null ? (
              <>
                <BoxHeader
                  title={
                    <FormattedMessage
                      id="grow.powerDetail.yourScore"
                      defaultMessage="Your Power score"
                    />
                  }
                />
                <p id="grow-power-noscore" className="text-smycles-body font-medium">
                  <FormattedMessage
                    id="grow.powerDetail.noScore"
                    defaultMessage="No Power score yet"
                  />
                </p>
                <p id="grow-power-noscore-hint" className="mt-1 max-w-[65ch] text-smycles-meta text-ink-muted">
                  {canUpload ? (
                    <FormattedMessage
                      id="grow.powerDetail.noScoreHint"
                      defaultMessage="Answer statements about this Power or analyse your work documents to begin."
                    />
                  ) : (
                    <FormattedMessage
                      id="grow.powerDetail.noScoreHintNoUpload"
                      defaultMessage="Answer statements about this Power to begin."
                    />
                  )}
                </p>
              </>
            ) : (
              <>
                <BoxHeader
                  variant="hero"
                  title={
                    <span className="flex items-baseline gap-4">
                      <span id="grow-power-score" className="font-serif font-normal tabular-nums">
                        {score}
                      </span>
                      <span className="text-smycles-title font-medium tracking-normal">
                        <FormattedMessage
                          id={
                            selfScore !== null && smartScore !== null
                              ? "grow.powerDetail.combinedScore"
                              : "grow.powerDetail.yourScore"
                          }
                          defaultMessage={
                            selfScore !== null && smartScore !== null
                              ? "Your combined Power score"
                              : "Your Power score"
                          }
                        />
                      </span>
                    </span>
                  }
                />
                {evaluation ? (
                  <p className="max-w-[70ch] text-smycles-body leading-relaxed text-ink">
                    {evaluation}
                  </p>
                ) : null}
              </>
            )}
            {data ? (
              <div
                id="grow-power-selfassessment"
                className="mt-6 space-y-3"
                data-assistant-anchor="grow.power.selfEvaluation"
              >
                <div className="grid grid-cols-1 items-center gap-3 @sm:grid-cols-[minmax(0,1fr)_auto] @sm:gap-4">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-smycles-body font-medium">
                      <FormattedMessage
                        id="grow.powerDetail.selfAssessment"
                        defaultMessage="Self-assessment"
                      />
                    </span>
                    <Tooltip content={<FormattedMessage id="grow.powerDetail.selfScoreSources" defaultMessage="This score uses your available answers from Power Quiz, other questionnaires and in-depth self-assessment." />}>
                      <span id="grow-power-selfscore" className="tabular-nums text-ink-muted">
                        {selfScore ?? "–"}
                      </span>
                    </Tooltip>
                    <span
                      id="grow-power-answered"
                      className="text-smycles-meta text-ink-muted"
                    >
                      {self && self.questionCount > 0 ? (
                        <FormattedMessage
                          id="power.instrument.inDepthStatements"
                          defaultMessage="{answered} / {total} in-depth statements"
                          values={{
                            answered: self.answeredQuestionCount,
                            total: self.questionCount,
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="grow.powerDetail.notStarted"
                          defaultMessage="In-depth assessment not started"
                        />
                      )}
                    </span>
                    <PowerQuizStatus status={quizStatus} />
                  </div>
                  <div className="flex items-stretch gap-2 @sm:items-center @sm:justify-end">
                    <Link
                      id="grow-power-selfassessment-action"
                      to={`/grow/${element}`}
                      className={buttonClasses({
                        variant: score === null ? "primary" : "secondary",
                        size: "compact",
                      })}
                    >
                      {selfAction}
                    </Link>
                    <PowerQuizButton status={quizStatus} />
                  </div>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-smycles-body font-medium">
                      <FormattedMessage
                        id="grow.powerDetail.smartAnalysis"
                        defaultMessage="Smart Analysis"
                      />
                    </span>
                    <span id="grow-power-smartscore" className="tabular-nums text-ink-muted">
                      {smartScore ?? "–"}
                    </span>
                    <span id="grow-power-smartstatus" className="text-smycles-meta text-ink-muted">
                      {smartScore !== null &&
                      typeof smart?.confidence === "number" ? (
                        <FormattedMessage
                          id="grow.powerDetail.confidence"
                          defaultMessage="Confidence: {percent}%"
                          values={{
                            percent: Math.round(smart.confidence * 100),
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="grow.powerDetail.noAnalysis"
                          defaultMessage="No analysis yet"
                        />
                      )}
                    </span>
                    {canUpload ? (
                      <span className="text-smycles-meta text-ink-muted"><AnalysedDocumentCount state={documentCount} /></span>
                    ) : null}
                  </div>
                  {canUpload ? <SmartAnalysisButton id="grow-power-analyse" /> : null}
                </div>
              </div>
            ) : null}
          </div>
          {smartScore !== null && (rationale || evidences.length > 0) ? (
            <section className="border-t border-ink/10 p-6">
              <p className="smycles-field-label text-ink">
                <FormattedMessage
                  id="grow.powerDetail.whatSmartFound"
                  defaultMessage="What Smart Analysis found"
                />
              </p>
              {rationale ? (
                <p className="mt-2 max-w-[70ch] text-smycles-body leading-relaxed text-ink">
                  {rationale}
                </p>
              ) : null}
              {evidences.length > 0 ? (
                <Disclosure
                  className={rationale ? "mt-6" : "mt-3"}
                  title={
                    <FormattedMessage
                      id="grow.powerDetail.evidenceTitle"
                      defaultMessage="Evidence"
                    />
                  }
                  count={evidences.length}
                >
                  <ul className="space-y-4">
                    {evidences.map((e, index) => (
                      <li
                        key={`${e.documentId ?? e.documentName}-${index}`}
                        className="border-b border-ink/10 pb-4 last:border-0 last:pb-0"
                      >
                        <p className="max-w-[70ch] text-smycles-body italic leading-relaxed text-ink">
                          “{e.quote}”
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-x-2 text-smycles-meta text-ink-muted">
                          {e.documentId ? (
                            <button
                              type="button"
                              className="underline underline-offset-2 hover:text-ink-accent focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink-accent-ui"
                              onClick={() => {
                                setOpenError(null);
                                void openDocument(e.documentId!);
                              }}
                              aria-label={intl.formatMessage(
                                {
                                  id: "grow.powerDetail.openDocumentNamed",
                                  defaultMessage: "Open document {name}",
                                },
                                { name: e.documentName },
                              )}
                            >
                              {e.documentName}
                            </button>
                          ) : (
                            <span>{e.documentName}</span>
                          )}
                          {e.pageNumber != null && e.pageNumber > 0 ? (
                            <span>
                              ·{" "}
                              <FormattedMessage
                                id="grow.powerDetail.page"
                                defaultMessage="Page {number}"
                                values={{ number: e.pageNumber }}
                              />
                            </span>
                          ) : null}
                          {e.stance ? (
                            <span>
                              ·{" "}
                              {e.stance === "PRO" ? (
                                <FormattedMessage
                                  id="grow.powerDetail.stancePro"
                                  defaultMessage="Pro"
                                />
                              ) : (
                                <FormattedMessage
                                  id="grow.powerDetail.stanceContra"
                                  defaultMessage="Contra"
                                />
                              )}
                            </span>
                          ) : null}
                          {e.support ? (
                            <span>· {String(e.support).toLowerCase()}</span>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {openError ? (
                    <p className="mt-2 text-smycles-meta text-ink-muted">
                      {openError}
                    </p>
                  ) : null}
                </Disclosure>
              ) : null}
            </section>
          ) : null}
        </section>
      </div>
    </main>
  );
}
